import { z } from "zod";
import { QueryParams } from "../../httpClient";
import { UrgencyEnum, MatchingStrategyEnum, LeadStatusEnum } from "./common";

// ==================
// Query Parameter Types
// ==================

/** Query params for listing leads (Admin) */
export interface AdminListLeadsQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
    status?: "pending" | "matched";
    expert_id?: number;
    client_id?: number;
}

export interface AdminLeadQueueQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
    project_id?: number;
    lead_id?: number;
    project_title?: string;
    status?: "pending" | "matched";
    expert_id?: number;
    client_id?: number;
    /** recent = inside the configured alert window, all = no upper bound. */
    needs_attention?: "recent" | "all";
    /** Which wait the needs_attention filter looks at; the API defaults to client. */
    attention_reason?: "client" | "expert";
}

/** Query params for listing leads (Client) */
export interface ClientListLeadsQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
    /** Filter by lead status: awaiting_match (pending), matched, or all (default) */
    status?: "awaiting_match" | "matched" | "all";
}

/** Query params for listing leads (Expert) */
export interface ExpertListLeadsQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
}

// ==================
// Request Body Schemas
// ==================

/** Request body for submitting a new lead (Client) */
export const CreateLeadRequestSchema = z.object({
    direct_thread_id: z.string().min(1).optional(),
    store_id: z.number().int().positive(),
    project_title: z.string().min(1).max(255),
    project_brief: z.string().optional(),
    urgency: UrgencyEnum,
    matching_strategy: MatchingStrategyEnum,
    selected_expert_id: z.number().int().positive().optional(),
    /** ID of a packaged service offered by the selected expert (only valid when expert is selected) */
    selected_packaged_service_id: z.number().int().positive().optional(),
    project_value_range_id: z.number().int().positive().optional(),
}).refine(
    (data) => {
        // selected_expert_id is required when matching_strategy is 'selected' or 'selected-auto'
        if (data.matching_strategy === "selected" || data.matching_strategy === "selected-auto") {
            return data.selected_expert_id !== undefined || data.direct_thread_id !== undefined;
        }
        return true;
    },
    {
        message: "selected_expert_id is required when matching_strategy is 'selected' or 'selected-auto'",
        path: ["selected_expert_id"],
    }
);

export type CreateLeadRequest = z.infer<typeof CreateLeadRequestSchema>;

/** Request body for experts inviting a client into a new lead/project */
export const CreateExpertLeadInviteRequestSchema = z.object({
    direct_thread_id: z.string().min(1).optional(),
    email: z.email(),
    first_name: z.string().min(1).max(255),
    last_name: z.string().min(1).max(255),
    brand_name: z.string().min(1).max(255).optional(),
    store_url: z.string().min(1).max(500),
    project_title: z.string().min(1).max(255),
    project_brief: z.string().max(65535).optional(),
    urgency: UrgencyEnum,
    timezone: z.string().max(255).optional(),
    selected_packaged_service_id: z.number().int().positive().optional(),
    project_value_range_id: z.number().int().positive().optional(),
});

export type CreateExpertLeadInviteRequest = z.infer<typeof CreateExpertLeadInviteRequestSchema>;

/** Request body for matching experts to a lead (Admin) */
export const MatchExpertsRequestSchema = z.object({
    expert_ids: z.array(z.number().int().positive()).min(1).max(3),
    mark_as_matched: z.boolean().optional().default(false),
}).refine(
    (data) => {
        // No duplicate expert IDs
        const unique = new Set(data.expert_ids);
        return unique.size === data.expert_ids.length;
    },
    {
        message: "Duplicate expert IDs in request.",
        path: ["expert_ids"],
    }
);

export type MatchExpertsRequest = z.infer<typeof MatchExpertsRequestSchema>;

/** Request body for updating a lead (Admin) */
export const AdminUpdateLeadRequestSchema = z.object({
    project_title: z.string().max(255).optional(),
    project_brief: z.string().max(65535).optional(),
    urgency: UrgencyEnum.optional(),
    project_value_range_id: z.number().int().positive().optional().nullable(),
});

export type AdminUpdateLeadRequest = z.infer<typeof AdminUpdateLeadRequestSchema>;
